/**
 * CitationList - Literature Citation List Display
 * ============================================================================
 * DC-1006: Displays a list of literature citations with confidence badge,
 * source indicator, and show more/collapse functionality.
 *
 * Features:
 * - Confidence level badge (high/moderate/expert_guidance)
 * - Source indicator (curated/eutils/mcp)
 * - List of CitationCards sorted by relevance
 * - "Show X more" button when citations exceed maxVisible
 * - Collapse/expand toggle for the entire list
 *
 * @version 1.0.0
 * @created 2026-01-24
 * ============================================================================
 */

import React, { useState, useMemo } from 'react';
import type { LiteratureSupport } from '../../types/bridgeIntelligence';
import { CitationCard } from './CitationCard';
import './citation.css';

// ============================================================================
// Types
// ============================================================================

export interface CitationListProps {
  /** Literature support data with citations */
  literatureSupport: LiteratureSupport;
  /** Maximum number of citations to show initially (default: 3) */
  maxVisible?: number;
  /** Start with the list collapsed (default: false) */
  defaultCollapsed?: boolean;
  /** Compact mode for smaller layouts */
  compact?: boolean;
  /** Custom className */
  className?: string;
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Get confidence badge class based on level
 */
const getConfidenceClass = (level: string): string => {
  switch (level) {
    case 'high':
      return 'citation-list__confidence-badge--high';
    case 'moderate':
      return 'citation-list__confidence-badge--moderate';
    case 'expert_guidance':
      return 'citation-list__confidence-badge--expert_guidance';
    default:
      return '';
  }
};

/**
 * Get human-readable confidence label
 */
const getConfidenceLabel = (level: string): string => {
  switch (level) {
    case 'high':
      return 'High Confidence';
    case 'moderate':
      return 'Moderate Confidence';
    case 'expert_guidance':
      return 'Expert Guidance';
    default:
      return level;
  }
};

/**
 * Get human-readable source label
 */
const getSourceLabel = (source: string): string => {
  switch (source) {
    case 'curated':
      return 'Curated Library';
    case 'eutils':
      return 'PubMed Search';
    case 'mcp':
      return 'PubMed MCP';
    case 'manual':
      return 'Manual Entry';
    default:
      return source;
  }
};

// ============================================================================
// Main Component
// ============================================================================

export const CitationList: React.FC<CitationListProps> = ({
  literatureSupport,
  maxVisible = 3,
  defaultCollapsed = false,
  compact = false,
  className = '',
}) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const [showAll, setShowAll] = useState(false);

  // Sort citations by relevance score (descending)
  const sortedCitations = useMemo(() => {
    return [...literatureSupport.citations].sort(
      (a, b) => b.relevance_score - a.relevance_score
    );
  }, [literatureSupport.citations]);

  // Determine which citations to display
  const visibleCitations = useMemo(() => {
    if (showAll) return sortedCitations;
    return sortedCitations.slice(0, maxVisible);
  }, [sortedCitations, showAll, maxVisible]);

  const hiddenCount = sortedCitations.length - maxVisible;
  const hasMore = hiddenCount > 0;

  // Don't render if no citations
  if (sortedCitations.length === 0) {
    return null;
  }

  return (
    <div className={`citation-list ${className}`}>
      {/* Header */}
      <div className="citation-list__header">
        <h5 className="citation-list__title">
          <span className="citation-list__title-icon">📚</span>
          Supporting Literature ({sortedCitations.length})
        </h5>

        <div className="citation-list__confidence">
          <span
            className={`citation-list__confidence-badge ${getConfidenceClass(literatureSupport.confidence_level)}`}
          >
            {getConfidenceLabel(literatureSupport.confidence_level)}
          </span>
          <span className="citation-list__source">
            via {getSourceLabel(literatureSupport.source)}
          </span>
        </div>

        <button
          className="citation-list__toggle"
          onClick={(e) => {
            e.stopPropagation();
            setIsCollapsed(!isCollapsed);
          }}
          aria-expanded={!isCollapsed}
          aria-label={isCollapsed ? 'Expand citations' : 'Collapse citations'}
        >
          {isCollapsed ? 'Show' : 'Hide'}
          <span className="citation-list__toggle-icon">
            {isCollapsed ? '▼' : '▲'}
          </span>
        </button>
      </div>

      {/* Citations */}
      {!isCollapsed && (
        <>
          <div className="citation-list__citations">
            {visibleCitations.map((citation) => (
              <CitationCard
                key={citation.pmid}
                citation={citation}
                compact={compact}
              />
            ))}
          </div>

          {/* Show More Button */}
          {hasMore && !showAll && (
            <button
              className="citation-list__show-more"
              onClick={(e) => {
                e.stopPropagation();
                setShowAll(true);
              }}
            >
              Show {hiddenCount} more citation{hiddenCount > 1 ? 's' : ''}
            </button>
          )}

          {/* Show Less Button (when expanded) */}
          {showAll && hasMore && (
            <button
              className="citation-list__show-more"
              onClick={(e) => {
                e.stopPropagation();
                setShowAll(false);
              }}
            >
              Show fewer citations
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default CitationList;
